/**
 * useFluxNodeActiveAlerts Hook
 *
 * Manages active (unresolved) FluxNode alerts with actions to resolve/dismiss.
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase/client';
import { useAuthStore } from '../store/authStore';

export interface FluxNodeAlert {
  id: string;
  user_id: string;
  alert_type: string;
  wallet_address: string | null;
  node_key: string | null;
  app_name: string | null;
  title: string;
  message: string | null;
  details: Record<string, unknown> | null;
  notification_sent: boolean;
  created_at: string;
  resolved: boolean;
  resolved_at: string | null;
  resolution_type: string | null;
  resolution_note: string | null;
}

export interface OfflineNode {
  id: string;
  user_id: string;
  wallet_address: string;
  node_key: string;
  ip_address: string | null;
  tier: string;
  status: string;
  rank: number | null;
  updated_at: string;
  last_seen_at: string | null;
}

export type ResolutionType = 'acknowledged' | 'node_removed' | 'auto_resolved' | 'dismissed';

export function useFluxNodeActiveAlerts() {
  const { isAuthenticated, session } = useAuthStore();
  const [activeAlerts, setActiveAlerts] = useState<FluxNodeAlert[]>([]);
  const [offlineNodes, setOfflineNodes] = useState<OfflineNode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const getAuthHeaders = useCallback(() => {
    if (!session?.access_token) return null;
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    };
  }, [session?.access_token]);

  // Load active alerts
  const loadActiveAlerts = useCallback(async () => {
    if (!isAuthenticated || !session?.access_token) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const headers = getAuthHeaders();
      if (!headers) return;

      // Fetch active alerts and offline nodes in parallel
      const [alertsRes, nodesRes] = await Promise.all([
        fetch('/api/v1/fluxnode/alerts?active=true', { headers }),
        fetch('/api/v1/fluxnode/alerts?offline_nodes=true', { headers }),
      ]);

      if (alertsRes.ok) {
        const alertsData = await alertsRes.json();
        setActiveAlerts(alertsData.data || []);
      }

      if (nodesRes.ok) {
        const nodesData = await nodesRes.json();
        setOfflineNodes(nodesData.data || []);
      }

      setError(null);
    } catch (err) {
      console.error('[FluxNode Active Alerts] Error loading:', err);
      setError(err instanceof Error ? err : new Error('Failed to load active alerts'));
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, session?.access_token, getAuthHeaders]);

  // Resolve specific alerts
  const resolveAlerts = useCallback(async (
    alertIds: string[],
    resolutionType: ResolutionType,
    resolutionNote?: string
  ) => {
    const headers = getAuthHeaders();
    if (!headers) return false;

    try {
      setIsProcessing(true);

      const response = await fetch('/api/v1/fluxnode/alerts/resolve', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          alert_ids: alertIds,
          resolution_type: resolutionType,
          resolution_note: resolutionNote,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to resolve alerts');
      }

      // Remove resolved alerts from state
      setActiveAlerts(prev => prev.filter(alert => !alertIds.includes(alert.id)));

      return true;
    } catch (err) {
      console.error('[FluxNode Active Alerts] Error resolving:', err);
      setError(err instanceof Error ? err : new Error('Failed to resolve alerts'));
      return false;
    } finally {
      setIsProcessing(false);
    }
  }, [getAuthHeaders]);

  // Remove node from monitoring
  const removeNode = useCallback(async (nodeKey: string, reason?: string) => {
    const headers = getAuthHeaders();
    if (!headers) return false;

    try {
      setIsProcessing(true);

      const response = await fetch('/api/v1/fluxnode/alerts/resolve?action=remove_node', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          node_key: nodeKey,
          reason,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to remove node');
      }

      // Remove node and related alerts from state
      setOfflineNodes(prev => prev.filter(node => node.node_key !== nodeKey));
      setActiveAlerts(prev => prev.filter(alert => alert.node_key !== nodeKey));

      return true;
    } catch (err) {
      console.error('[FluxNode Active Alerts] Error removing node:', err);
      setError(err instanceof Error ? err : new Error('Failed to remove node'));
      return false;
    } finally {
      setIsProcessing(false);
    }
  }, [getAuthHeaders]);

  // Resolve all alerts for a node
  const resolveAllForNode = useCallback(async (
    nodeKey: string,
    resolutionType: ResolutionType
  ) => {
    const headers = getAuthHeaders();
    if (!headers) return false;

    try {
      setIsProcessing(true);

      const response = await fetch('/api/v1/fluxnode/alerts/resolve?action=resolve_all_for_node', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          node_key: nodeKey,
          resolution_type: resolutionType,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to resolve alerts');
      }

      // Remove resolved alerts from state
      setActiveAlerts(prev => prev.filter(alert => alert.node_key !== nodeKey));

      return true;
    } catch (err) {
      console.error('[FluxNode Active Alerts] Error resolving for node:', err);
      setError(err instanceof Error ? err : new Error('Failed to resolve alerts'));
      return false;
    } finally {
      setIsProcessing(false);
    }
  }, [getAuthHeaders]);

  // Dismiss all alerts (mark as acknowledged)
  const dismissAllAlerts = useCallback(async () => {
    if (activeAlerts.length === 0) return true;

    const alertIds = activeAlerts.map(a => a.id);
    return resolveAlerts(alertIds, 'dismissed');
  }, [activeAlerts, resolveAlerts]);

  // Load on mount and when auth changes
  useEffect(() => {
    loadActiveAlerts();
  }, [loadActiveAlerts]);

  // Group alerts by node for better UX
  const alertsByNode = activeAlerts.reduce((acc, alert) => {
    const key = alert.node_key || 'other';
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(alert);
    return acc;
  }, {} as Record<string, FluxNodeAlert[]>);

  return {
    activeAlerts,
    offlineNodes,
    alertsByNode,
    isLoading,
    isProcessing,
    error,
    isAuthenticated,
    hasActiveAlerts: activeAlerts.length > 0,
    hasOfflineNodes: offlineNodes.length > 0,
    resolveAlerts,
    removeNode,
    resolveAllForNode,
    dismissAllAlerts,
    refetch: loadActiveAlerts,
  };
}
